import React from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Typography, Button } from '@mui/material';
import SortableTableHeader from './SortableTableHeader';
import { OffersReportSortInput, ReportWLOfferListViewFragment } from '__generated__/graphql';
import { THandleSortChange } from '../hooks/useReportFilters';
import { Link } from 'react-router-dom';
import i18next from 'i18next';
import { getMuiTablePaginationI18nProps } from 'components/table/muiTablePaginationI18nProps';
import { formatPrice } from 'components/Price';
import { tableData, tableHeaders } from '../hooks/useReportData';
import { NO_PRICE } from 'semshared/pricelist/quickprice';

interface ReportTableProps {
  results: ReportWLOfferListViewFragment[];
  loading: boolean;
  pagination: {
    page: number;
    rowsPerPage: number;
    totalCount: number;
  };
  sortFields: OffersReportSortInput[];
  handleSort: THandleSortChange;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  allResultsTotals?: {
    singleRooms: number;
    doubleRooms: number;
    guests: number;
    totalPriceNet: number;
    totalPriceGross: number;
    commissionAmountNet: number;
  } | null;
}

/**
 * Table component for displaying offer report results.
 * Supports sorting and pagination. Uses tableData/tableHeaders hooks for column configuration.
 */
export default function ReportTable({ results, loading, pagination, sortFields, handleSort, onPageChange, onRowsPerPageChange, allResultsTotals }: ReportTableProps) {
  if (loading || results.length === 0) {
    return null;
  }

  const data = tableData(results);
  const renderMoney = (value: number | null | undefined) => {
    if (value == null || Number.isNaN(value) || value === NO_PRICE) return 'N/A';
    return formatPrice(value);
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {i18next.t('statistics-results')} ({pagination.totalCount} {i18next.t('statistics-entries-found')})
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {tableHeaders.filter(header => !header.skipTable).map((header) => (
                <SortableTableHeader
                  key={header.field}
                  field={header.field}
                  sortField={header.sortField ? header.sortField as keyof OffersReportSortInput : undefined}
                  label={header.label}
                  sortFields={sortFields}
                  onSort={handleSort}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map(result => (
              <TableRow key={result.id}>
                <TableCell>{result._createdAt}</TableCell>
                <TableCell>
                  <Link to={`/offers/${result.id}`}>
                    {result.offerCode || 'N/A'}
                  </Link>
                </TableCell>
                <TableCell>{result.status ? i18next.t(`enums-EOfferStatus-${result.status}`) : 'N/A'}</TableCell>
                <TableCell>{result.latestVersionReport?.status ? i18next.t(`enums-EOfferVersionStatus-${result.latestVersionReport.status}`) : 'N/A'}</TableCell>
                <TableCell>
                  {result.booking?.refCode ? (
                    <Link to={`/bookings/${result.booking.id}`}>
                      {result.booking?.refCode}
                    </Link>
                  ) : 'N/A'}
                </TableCell>
                <TableCell>{result.booking?.status ? i18next.t(`enums-EHLBookingStatus-${result.booking?.status}`) : 'N/A'}</TableCell>
                <TableCell>{result.booking?.title || 'N/A'}</TableCell>
                <TableCell>{result.publicCompany?.name || 'N/A'}</TableCell>
                <TableCell>{result.publicCompany?.id || 'N/A'}</TableCell>
                <TableCell>{result.publicCompany?.country || 'N/A'}</TableCell>
                <TableCell>
                  {result._clientId && result._client ? (
                    <Link to={`/clients/clients/${result._clientId}`} style={{ textDecoration: 'none', color: '#AC2A6E' }}>
                      {result._client}
                    </Link>
                  ) : (
                    result._client || 'N/A'
                  )}
                </TableCell>
                <TableCell>{result._clientId || 'N/A'}</TableCell>
                <TableCell>{result.requestedBy || 'N/A'}</TableCell>
                <TableCell>{result.client?.email || 'N/A'}</TableCell>
                <TableCell>
                  {result._seminarDate
                    ? result._seminarDate
                    : 'N/A'}
                </TableCell>
                <TableCell>
                  {result.hotel?.id && result.hotel?.name ? (
                    <Link to={`/settings/hotels/${result.hotel.id}`} style={{ textDecoration: 'none', color: '#AC2A6E' }}>
                      {result.hotel.name}
                    </Link>
                  ) : (
                    result.hotel?.name || 'N/A'
                  )}
                </TableCell>
                <TableCell>{result._singleRooms || 'N/A'}</TableCell>
                <TableCell>{result._doubleRooms || 'N/A'}</TableCell>
                <TableCell>{result._guests || 'N/A'}</TableCell>
                <TableCell>{renderMoney(result.latestVersion?.totalPriceNet)}</TableCell>
                <TableCell>{renderMoney(result.latestVersion?.totalPriceGross)}</TableCell>
                <TableCell>{renderMoney(result.commissionAmountNet)}</TableCell>
                <TableCell>{result._ivApprovedAt || 'N/A'}</TableCell>
              </TableRow>
            ))}

            {/* Totals Row - Shows totals for ALL filtered results across all pages */}
            {allResultsTotals && (
              <TableRow sx={{ backgroundColor: '#e0e0e0', fontWeight: 'bold', borderTop: 2, borderColor: '#666' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>{i18next.t('statistics-total-sum')}</TableCell>
                <TableCell colSpan={15}></TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{allResultsTotals.singleRooms}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{allResultsTotals.doubleRooms}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{allResultsTotals.guests}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{formatPrice(allResultsTotals.totalPriceNet)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{formatPrice(allResultsTotals.totalPriceGross)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{formatPrice(allResultsTotals.commissionAmountNet)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={pagination.totalCount}
        page={pagination.page}
        onPageChange={(event, newPage) => onPageChange(newPage)}
        rowsPerPage={pagination.rowsPerPage}
        onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
        rowsPerPageOptions={[25, 50, 100, 250]}
        {...getMuiTablePaginationI18nProps()}
      />
    </Box>
  );
}